import { DefaultRolesFieldNames, UserReplacementFieldNames } from "@/enums";
import { buildWorksheetsFromTables, exportWorksheetsToXLSX } from "@/helpers";
import { useMapGuidsToValues } from "@/hooks";
import { useFormPropertyValueLookupProvider } from "@/providers";
import { useGetUsersByIdsMutation } from "@/services";
import { faFileExport } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "antd";
import dayjs from "dayjs";
import { arrayOf, shape, string } from "prop-types";
import { useIntl } from "react-intl";

export const ExportUserReplacementQuery = ({
  userReplacedId,
  data,
  defaultRolesData,
}) => {
  const { getPropertyValueDisplay } = useFormPropertyValueLookupProvider();
  const intl = useIntl();

  const [getUsersByUserIds] = useGetUsersByIdsMutation();

  const setupUserReplacementTables = (userName, data) => {
    const headers = Object.keys(UserReplacementFieldNames);

    const tables = [
      {
        data: [{ UserToReplace: userName }],
        headers: ["User To Replace"],
      },
      {
        data: data,
        headers: headers,
        skipHeader: false,
      },
    ];

    return tables;
  };

  const setupDefaultRolesTables = (userName, data) => {
    const headers = Object.keys(DefaultRolesFieldNames);

    const tables = [
      {
        data: [{ UserToReplace: userName }],
        headers: ["Default Roles"],
      },
      {
        data: data,
        headers: headers,
        skipHeader: false,
      },
    ];

    return tables;
  };

  const mappedUserReplacementData = useMapGuidsToValues(
    data,
    [
      UserReplacementFieldNames.ProjectStatus,
      UserReplacementFieldNames.ProductCategory,
      UserReplacementFieldNames.ProjectType,
    ],
    getPropertyValueDisplay
  );

  const mappedDefaultRolesData = useMapGuidsToValues(
    defaultRolesData,
    [DefaultRolesFieldNames.FunctionalArea, DefaultRolesFieldNames.Region],
    getPropertyValueDisplay
  );

  const generateUserReplacementExportData = () => {
    const filterSecure = mappedUserReplacementData.filter(
      (rows) => rows.secure === false
    );

    return filterSecure;
  };

  const handleExport = () => {
    const userReplacementExportData = generateUserReplacementExportData();
    const sheetNames = ["User Replacement", "Default Roles"];

    //Get user then export file
    let userName = null;
    getUsersByUserIds([userReplacedId])
      .unwrap()
      .then((payload) => {
        const user = payload[0];
        userName = user?.displayName ?? `${user?.surname}, ${user?.givenName}`;
        const userReplacementTables = setupUserReplacementTables(
          userName,
          userReplacementExportData
        );
        const defaultRolesTables = setupDefaultRolesTables(
          userName,
          mappedDefaultRolesData
        );
        const worksheets = [];
        const userReplacementWorksheet = buildWorksheetsFromTables(
          userReplacementTables,
          UserReplacementFieldNames
        );
        worksheets.push(userReplacementWorksheet);
        const defaultRolesWorksheet = buildWorksheetsFromTables(
          defaultRolesTables,
          DefaultRolesFieldNames
        );
        worksheets.push(defaultRolesWorksheet);
        const fileName = `Replace-${userName}-${dayjs().format()}.xlsx`;
        exportWorksheetsToXLSX(fileName, sheetNames, worksheets);
      });
  };

  return (
    <Button
      data-test-id="export-xlsx-btn"
      onClick={handleExport}
      icon={<FontAwesomeIcon icon={faFileExport} />}
    >
      {intl.formatMessage({ id: "general.export" })}
    </Button>
  );
};

ExportUserReplacementQuery.propTypes = {
  userReplacedId: string.isRequired,
  data: arrayOf(shape()),
  defaultRolesData: arrayOf(shape()),
};
